import apiClient from './api';
import type {
  Contract,
  ContractParty,
  ContractComment,
  ContractListParams,
  ContractListResponse,
  CreateContractData,
  UpdateContractData,
  CreateContractPartyData,
  UpdateContractPartyData,
  CreateContractCommentData,
  ContractDashboardStats,
  ContractMilestone,
  ApprovalAction,
  DigitalSignatureData,
  ContractStatus,
  ContractAnalytics,
  ContractTrends,
  ContractPerformance,
} from '../types/contracts';

class ContractService {
  private baseURL = '/contracts';
  private partiesURL = '/contract-parties';

  // Contract CRUD operations
  async getContractsByProject(
    projectId: string,
    params?: ContractListParams
  ): Promise<ContractListResponse> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(item => queryParams.append(key, item.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    // Use the new project-specific endpoint
    const response = await apiClient.get(
      `/projects/${projectId}/contracts?${queryParams.toString()}`
    );
    return response.data.data;
  }

  async getAllContracts(params?: ContractListParams): Promise<ContractListResponse> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(item => queryParams.append(key, item.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    // Use the global endpoint
    const response = await apiClient.get(`${this.baseURL}?${queryParams.toString()}`);
    return response.data.data;
  }

  async getContractById(contractId: string): Promise<Contract> {
    const response = await apiClient.get(`${this.baseURL}/${contractId}`);
    return response.data.data;
  }

  async createContract(contractData: CreateContractData): Promise<Contract> {
    // If projectId is provided, use project-specific endpoint
    if (contractData.projectId) {
      const response = await apiClient.post(
        `/projects/${contractData.projectId}/contracts`,
        contractData
      );
      return response.data.data;
    } else {
      // Use global endpoint
      const response = await apiClient.post(this.baseURL, contractData);
      return response.data.data;
    }
  }

  async updateContract(contractId: string, contractData: UpdateContractData): Promise<Contract> {
    const response = await apiClient.put(`${this.baseURL}/${contractId}`, contractData);
    return response.data.data;
  }

  async deleteContract(contractId: string): Promise<void> {
    await apiClient.delete(`${this.baseURL}/${contractId}`);
  }

  async duplicateContract(contractId: string): Promise<Contract> {
    const response = await apiClient.post(`${this.baseURL}/${contractId}/duplicate`);
    return response.data.data;
  }

  // Milestone management
  async addMilestone(contractId: string, milestone: Partial<ContractMilestone>): Promise<Contract> {
    const response = await apiClient.post(`${this.baseURL}/${contractId}/milestones`, milestone);
    return response.data.data;
  }

  async updateMilestone(
    contractId: string,
    milestoneId: string,
    milestone: Partial<ContractMilestone>
  ): Promise<Contract> {
    const response = await apiClient.put(
      `${this.baseURL}/${contractId}/milestones/${milestoneId}`,
      milestone
    );
    return response.data.data;
  }

  // Version control
  async addVersion(
    contractId: string,
    versionData: {
      fileName: string;
      fileUrl: string;
      fileSize: number;
      changeLog: string;
    }
  ): Promise<Contract> {
    const response = await apiClient.post(`${this.baseURL}/${contractId}/versions`, versionData);
    return response.data.data;
  }

  // Approval workflow
  async processApproval(contractId: string, approval: ApprovalAction): Promise<Contract> {
    const response = await apiClient.post(`${this.baseURL}/${contractId}/approve`, approval);
    return response.data.data;
  }

  // Digital signature
  async addSignature(contractId: string, signatureData: DigitalSignatureData): Promise<Contract> {
    const response = await apiClient.post(`${this.baseURL}/${contractId}/sign`, signatureData);
    return response.data.data;
  }

  // Dashboard and statistics
  async getDashboardStats(projectId?: string): Promise<ContractDashboardStats> {
    if (projectId) {
      // Project-specific dashboard using project routes
      const response = await apiClient.get(`/projects/${projectId}/contracts/dashboard`);
      return response.data.data;
    } else {
      // Global dashboard
      const response = await apiClient.get(`${this.baseURL}/dashboard`);
      return response.data.data;
    }
  }

  // Contract activity
  async getContractActivity(contractId: string, params?: { page?: number; limit?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await apiClient.get(
      `${this.baseURL}/${contractId}/activity?${queryParams.toString()}`
    );
    return response.data.data;
  }

  // Export contract
  async exportContract(contractId: string, format: 'pdf' | 'excel' | 'json' = 'pdf') {
    const response = await apiClient.get(`${this.baseURL}/${contractId}/export?format=${format}`);
    return response.data.data;
  }

  // Get contracts requiring action
  async getContractsRequiringAction(params?: { page?: number; limit?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await apiClient.get(
      `${this.baseURL}/requiring-action?${queryParams.toString()}`
    );
    return response.data.data;
  }

  // Bulk operations
  async bulkUpdateStatus(contractIds: string[], status: ContractStatus) {
    const response = await apiClient.patch(`${this.baseURL}/bulk/status`, {
      contractIds,
      status,
    });
    return response.data.data;
  }

  // Contract Parties
  async getContractParties(params?: any) {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await apiClient.get(`${this.partiesURL}?${queryParams.toString()}`);
    return response.data.data;
  }

  async getContractPartyById(partyId: string): Promise<ContractParty> {
    const response = await apiClient.get(`${this.partiesURL}/${partyId}`);
    return response.data.data;
  }

  async createContractParty(partyData: CreateContractPartyData): Promise<ContractParty> {
    const response = await apiClient.post(this.partiesURL, partyData);
    return response.data.data;
  }

  async updateContractParty(
    partyId: string,
    partyData: UpdateContractPartyData
  ): Promise<ContractParty> {
    const response = await apiClient.put(`${this.partiesURL}/${partyId}`, partyData);
    return response.data.data;
  }

  async deleteContractParty(partyId: string): Promise<void> {
    await apiClient.delete(`${this.partiesURL}/${partyId}`);
  }

  async activateContractParty(partyId: string): Promise<ContractParty> {
    const response = await apiClient.patch(`${this.partiesURL}/${partyId}/activate`);
    return response.data.data;
  }

  async getContractPartyStats() {
    const response = await apiClient.get(`${this.partiesURL}/stats`);
    return response.data.data;
  }

  async searchContractParties(query: string, type?: 'internal' | 'external', limit?: number) {
    const params = new URLSearchParams();
    params.append('q', query);
    if (type) params.append('type', type);
    if (limit) params.append('limit', limit.toString());

    const response = await apiClient.get(`${this.partiesURL}/search?${params.toString()}`);
    return response.data.data;
  }

  async getContractsByParty(partyId: string, params?: any) {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await apiClient.get(
      `${this.partiesURL}/${partyId}/contracts?${queryParams.toString()}`
    );
    return response.data.data;
  }

  async bulkDeactivateParties(partyIds: string[]) {
    const response = await apiClient.patch(`${this.partiesURL}/bulk/deactivate`, { partyIds });
    return response.data.data;
  }

  // Contract Comments
  async getContractComments(contractId: string, params?: any) {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(item => queryParams.append(key, item.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const response = await apiClient.get(
      `/contracts/${contractId}/comments?${queryParams.toString()}`
    );
    return response.data.data;
  }

  async getCommentById(commentId: string): Promise<ContractComment> {
    const response = await apiClient.get(`/comments/${commentId}`);
    return response.data.data;
  }

  async addContractComment(
    contractId: string,
    comment: CreateContractCommentData
  ): Promise<ContractComment> {
    const response = await apiClient.post(`/contracts/${contractId}/comments`, comment);
    return response.data.data;
  }

  async updateContractComment(commentId: string, content: string): Promise<ContractComment> {
    const response = await apiClient.put(`/comments/${commentId}`, { content });
    return response.data.data;
  }

  async deleteContractComment(commentId: string): Promise<void> {
    await apiClient.delete(`/comments/${commentId}`);
  }

  async getCommentReplies(commentId: string, params?: any) {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await apiClient.get(
      `/comments/${commentId}/replies?${queryParams.toString()}`
    );
    return response.data.data;
  }

  async toggleCommentReaction(commentId: string, reaction: 'like' | 'dislike' = 'like') {
    const response = await apiClient.post(`/comments/${commentId}/react`, { reaction });
    return response.data.data;
  }

  async searchComments(query: string, params?: any) {
    const queryParams = new URLSearchParams();
    queryParams.append('q', query);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' && key !== 'q') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await apiClient.get(`/comments/search?${queryParams.toString()}`);
    return response.data.data;
  }

  async getContractCommentStats(contractId: string) {
    const response = await apiClient.get(`/contracts/${contractId}/comments/stats`);
    return response.data.data;
  }

  // File upload utilities
  async uploadContractFile(
    file: File,
    contractId?: string
  ): Promise<{ fileUrl: string; fileName: string; fileSize: number }> {
    const formData = new FormData();
    formData.append('file', file);
    if (contractId) formData.append('contractId', contractId);

    const response = await apiClient.post('/upload/contract', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.data;
  }

  async uploadSignatureImage(file: File): Promise<{ signatureImageUrl: string }> {
    const formData = new FormData();
    formData.append('signature', file);

    const response = await apiClient.post('/upload/signature', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.data;
  }

  // Helper methods
  getStatusColor(status: ContractStatus): string {
    const colors = {
      draft: 'gray',
      review: 'yellow',
      approved: 'blue',
      active: 'green',
      completed: 'green',
      expired: 'red',
      cancelled: 'gray',
      rejected: 'red',
    };
    return colors[status] || 'gray';
  }

  getStatusLabel(status: ContractStatus): string {
    const labels = {
      draft: 'Draft',
      review: 'Under Review',
      approved: 'Approved',
      active: 'Active',
      completed: 'Completed',
      expired: 'Expired',
      cancelled: 'Cancelled',
      rejected: 'Rejected',
    };
    return labels[status] || status;
  }

  formatCurrency(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  }

  calculateProgress(milestones: ContractMilestone[]): number {
    if (!milestones || milestones.length === 0) return 0;

    const completed = milestones.filter(m => m.status === 'completed').length;
    return Math.round((completed / milestones.length) * 100);
  }

  isContractExpired(contract: Contract): boolean {
    if (!contract.endDate) return false;
    const endDate = new Date(contract.endDate);
    const now = new Date();
    return endDate < now && !['completed', 'cancelled'].includes(contract.status);
  }

  getDaysUntilExpiration(contract: Contract): number | null {
    if (!contract.endDate) return null;
    const endDate = new Date(contract.endDate);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  canUserApprove(contract: Contract, userId: string): boolean {
    if (!contract.approvals || contract.approvals.length === 0) return false;

    return contract.approvals.some(
      approval =>
        approval.userId === userId &&
        approval.status === 'pending' &&
        approval.order === contract.currentApprovalStep + 1
    );
  }

  canUserSign(contract: Contract, userId: string): boolean {
    return (
      contract.status === 'approved' &&
      !contract.allPartiesSigned &&
      !contract.signatures.some(sig => sig.userId === userId)
    );
  }

  // Analytics endpoints (only available globally, not project-specific)
  async getAnalytics(params?: {
    projectId?: string;
    dateRange?: string;
    status?: string;
    type?: string;
  }): Promise<ContractAnalytics> {
    // Analytics are only available on global endpoint
    const response = await apiClient.get(`${this.baseURL}/analytics`, { params });
    return response.data.data;
  }

  async getTrends(params?: {
    projectId?: string;
    period?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  }): Promise<ContractTrends> {
    // Trends are only available on global endpoint
    const response = await apiClient.get(`${this.baseURL}/analytics/trends`, { params });
    return response.data.data;
  }

  async getPerformance(params?: { projectId?: string }): Promise<ContractPerformance> {
    // Performance are only available on global endpoint
    const response = await apiClient.get(`${this.baseURL}/analytics/performance`, { params });
    return response.data.data;
  }
}

export default new ContractService();
